/**
 * Giữ lại cuộc hội thoại Javis AI khi người dùng đóng khung chat rồi mở lại.
 *
 * <h2>Vì sao cần</h2>
 * <p>Trước đây đóng khung chat là xoá sạch: mở lại thành hội thoại mới, người dùng mất cả lịch sử
 * lẫn ngữ cảnh AI đang nhớ. Nay lưu {@code conversationId} + danh sách tin nhắn để mở lại là chat
 * tiếp đúng chỗ đang dở.</p>
 *
 * <h2>Vì sao là sessionStorage chứ không phải localStorage</h2>
 * <p>Nội dung chat có thể chứa thông tin tài chính của doanh nghiệp. {@code sessionStorage} bị xoá
 * khi đóng tab, còn {@code localStorage} nằm lại trên đĩa sau khi người dùng đăng xuất — máy dùng
 * chung thì người sau mở lại đọc được. Hội thoại phía JAVIS AI dù sao cũng chỉ sống 1 giờ, nên
 * không có gì để mất khi chọn phạm vi hẹp hơn.</p>
 *
 * <h2>Hết hạn</h2>
 * <p>Mốc hết hạn lấy từ {@code expiresAt} do server trả về lúc tạo hội thoại (hiện là +1 giờ, do
 * cấu hình assistant quyết định — client không đặt được). Quá mốc đó thì bỏ hẳn: gửi tiếp vào
 * hội thoại đã chết chỉ nhận {@code ERR-CONVERSATION-EXPIRED}.</p>
 */

/** Một dòng hội thoại đang hiển thị. */
export interface ChatMessage {
  id: string;
  role: "user" | "bot";
  content: string;
}

export interface StoredChatSession {
  conversationId: string;
  /** ISO — mốc hết hạn của hội thoại phía JAVIS AI. */
  expiresAt: string;
  /** ISO — thời điểm mở hội thoại, dùng cho nhãn "Hôm nay, HH:mm". */
  startedAt: string;
  /** Chủ của hội thoại. Đổi người đăng nhập trên cùng tab thì KHÔNG được khôi phục. */
  userId: string;
  messages: ChatMessage[];
}

const STORAGE_KEY = "javis-ai-chat-session";

/** Chặn phình sessionStorage khi ai đó chat rất dài; giữ phần gần nhất là đủ để hiểu mạch. */
const MAX_STORED_MESSAGES = 100;

/** Server không trả `expiresAt` thì tự suy 1 giờ — bằng đúng cấu hình hiện tại của assistant. */
const DEFAULT_TTL_MS = 60 * 60 * 1000;

export const expiresAtOf = (raw?: string | null): string => {
  if (raw) {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) return new Date(parsed).toISOString();
  }
  return new Date(Date.now() + DEFAULT_TTL_MS).toISOString();
};

/**
 * Hội thoại còn dùng được của đúng người đang đăng nhập.
 *
 * <p>Trả {@code null} khi: chưa có gì lưu, dữ liệu hỏng, đã hết hạn, hoặc của người dùng khác.
 * Mọi trường hợp đó đều dọn luôn bản lưu để lần sau không phải kiểm lại.</p>
 */
export const loadSession = (userId: string): StoredChatSession | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const saved = JSON.parse(raw) as StoredChatSession;
    const stillValid =
      saved?.conversationId &&
      saved?.userId === userId &&
      Date.parse(saved.expiresAt) > Date.now();

    if (!stillValid) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { ...saved, messages: saved.messages ?? [] };
  } catch {
    // JSON hỏng hoặc trình duyệt chặn storage (chế độ ẩn danh) — coi như chưa có phiên nào.
    return null;
  }
};

export const saveSession = (session: StoredChatSession): void => {
  try {
    // Bong bóng bot rỗng là tin nhắn đang stream dở lúc người dùng đóng khung chat — lưu lại thì
    // mở ra thấy một khoảng trống không giải thích được.
    const messages = session.messages
      .filter((msg) => msg.content)
      .slice(-MAX_STORED_MESSAGES);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...session, messages }));
  } catch {
    // Hết quota hoặc storage bị chặn — không lưu được thì thôi, không được làm hỏng khung chat.
  }
};

export const clearSession = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Không có gì để xử lý.
  }
};
